import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { getLang } from "../lib/i18n";
import { getCookieConsent } from "../lib/cookieConsent";

const syncLang = (u) => { if (u && u.lang !== getLang()) api.put("/auth/me/lang", { lang: getLang() }).catch(() => {}); };
const syncCookies = (u) => {
  const c = getCookieConsent();
  if (u && c && (!u.cookie_consent_at || new Date(c.at) > new Date(u.cookie_consent_at))) {
    api.put("/auth/me/cookies", { statistics: c.statistics, choice: c.choice }).catch(() => {});
  }
};
const syncPrefs = (u) => { syncLang(u); syncCookies(u); };

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not auth, {} = user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      syncPrefs(data.user);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const { data } = await api.post("/auth/login", payload);
    if (data.access_token) localStorage.setItem("tc_access_token", data.access_token);
    setUser(data.user);
    syncPrefs(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", { ...payload, lang: getLang(), cookie_statistics: getCookieConsent()?.statistics ?? null, cookie_choice: getCookieConsent()?.choice ?? null });
    if (data.access_token) localStorage.setItem("tc_access_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("tc_access_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
